import {
  DATE_PRESETS,
  ENABLED_TYPES,
  MAX_CUSTOM_DAYS,
  MAX_METRIC_KEYS,
  isBarShape,
} from '../helper/customAnalytics';

// Stand-in for the custom-metrics backend (api/customAnalytics.js), same contract: charts are
// stored per account in localStorage with numeric ids, and every call resolves to
// `{ data, status }` or rejects with an axios-shaped `{ response: { status, data: { detail } } }`.
// Counts are derived from (key, value, day) so a date range always gives the same chart.
// Times are UTC (the mock ignores `tz`); inbox filters are accepted but do not change counts.
//
// A chart reads its data one of two ways (`series_by`):
//   value  one metric, one entry per value of it (Star Rating 1 to 5)
//   key    several metrics, one entry per metric, each counting its events (Bot Messages vs
//          User Messages vs Sessions); modelled on the Botpress analytics page

const DAY = 86400;
const HOUR = 3600;
const MAX_CHARTS = 50;
const TOP_VALUES = 20;

const spread = (prefix, count, start = 1) =>
  Array.from({ length: count }, (_, i) => ({
    value: `${prefix}_${String(i + start).padStart(2, '0')}`,
    weight: Math.max(1, 12 - i * 0.4),
  }));

// A metric whose events carry no interesting value: one value named after it.
const counter = (name, weight) => ({
  spiky: true,
  values: [{ value: name, weight }],
});

// `spiky` makes some days several times busier, like the Botpress sessions chart.
const SOURCES = {
  'Star Rating': {
    values: [
      { value: '1', weight: 1 },
      { value: '2', weight: 1 },
      { value: '3', weight: 2 },
      { value: '4', weight: 4 },
      { value: '5', weight: 5 },
    ],
  },
  preference: {
    values: [
      { value: 'chicken rice', weight: 5 },
      { value: 'chicken spicy', weight: 3 },
      { value: 'fish', weight: 2 },
      { value: 'vegetarian', weight: 1 },
      { value: 'noodles', weight: 2 },
    ],
  },
  Rating: {
    values: [
      { value: '3 star rating (chatbot)', weight: 5 },
      { value: '4 star rating (chatbot)', weight: 3.3 },
      { value: '1 star rating (chatbot)', weight: 1.1 },
      { value: '4 star rating (live chat)', weight: 1.1 },
      { value: '2 star rating (chatbot)', weight: 0.6 },
    ],
  },
  country: { values: spread('country', 30) },
  'Bot Messages': counter('Bot Messages', 9),
  'User Messages': counter('User Messages', 3),
  Sessions: counter('Sessions', 4),
  'New Users': counter('New Users', 5),
  'Returning Users': counter('Returning Users', 1),
};

const hash = text => {
  let h = 7;
  for (let i = 0; i < text.length; i += 1) {
    h = (h * 31 + text.charCodeAt(i)) % 2147483647;
  }
  return h;
};

const dayCount = (key, item, day, spiky) => {
  const h = hash(`${key}|${item.value}|${day}`);
  let count = (h % 4) * item.weight;
  if (spiky && hash(`${key}|${day}`) % 9 === 0) count *= 4;
  return Math.round(count);
};

const hourCount = (key, item, hourStart, spiky) => {
  const dayShare = dayCount(
    key,
    item,
    Math.floor(hourStart / DAY) * DAY,
    spiky
  );
  const h = hash(`${key}|${item.value}|h${hourStart}`);
  return Math.round((dayShare / 24) * ((h % 5) / 2));
};

const fail = (status, code, message) =>
  Promise.reject(
    Object.assign(new Error(message), {
      response: { status, data: { detail: { code, message } } },
    })
  );

const respond = (data, status = 200) => Promise.resolve({ data, status });

// --- time ranges ----------------------------------------------------------------------------

const toIso = unix =>
  new Date(unix * 1000).toISOString().replace('.000Z', '+00:00');

const startOfUtcDay = unix => Math.floor(unix / DAY) * DAY;

const parseDate = value => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
  if (!match) return null;
  return (
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) / 1000
  );
};

const addMonths = (unix, months) => {
  const date = new Date(unix * 1000);
  date.setUTCMonth(date.getUTCMonth() + months);
  return Math.floor(date.getTime() / 1000);
};

// Resolves a time_range like the backend: [from, to) in unix seconds, or a rejected error.
const resolveRange = (timeRange, now) => {
  const range = timeRange || { preset: 'last_30_days' };
  const today = startOfUtcDay(now);
  const known = DATE_PRESETS.some(p => p.key === range.preset);
  if (!known && range.preset !== 'custom') {
    return { error: ['invalid_time_range', 'Unknown preset.'] };
  }
  if (range.preset !== 'custom' && (range.from || range.to)) {
    return { error: ['invalid_time_range', 'from/to only with custom.'] };
  }
  switch (range.preset) {
    case 'last_day':
      return { from: now - DAY, to: now };
    case 'last_7_days':
      return { from: today - 6 * DAY, to: now };
    case 'last_30_days':
      return { from: today - 29 * DAY, to: now };
    case 'last_90_days':
      return { from: today - 89 * DAY, to: now };
    case 'last_6_months':
      return { from: addMonths(today, -6), to: now };
    case 'last_year':
      return { from: addMonths(today, -12), to: now };
    default: {
      const from = parseDate(range.from);
      const to = parseDate(range.to);
      if (from === null || to === null || from > to) {
        return { error: ['invalid_time_range', 'Bad custom range.'] };
      }
      if ((to - from) / DAY + 1 > MAX_CUSTOM_DAYS) {
        return { error: ['range_too_large', 'Pick at most 400 days.'] };
      }
      return { from, to: to + DAY };
    }
  }
};

const autoInterval = (timeRange, from, to) => {
  switch (timeRange?.preset) {
    case 'last_day':
      return 'hour';
    case 'last_6_months':
      return 'month';
    case 'last_year':
      return 'week';
    case 'custom': {
      const days = (to - from) / DAY;
      if (days <= 2) return 'hour';
      return days <= 120 ? 'day' : 'week';
    }
    default:
      return 'day';
  }
};

const bucketStart = (unix, interval) => {
  if (interval === 'hour') return Math.floor(unix / HOUR) * HOUR;
  const day = startOfUtcDay(unix);
  if (interval === 'day') return day;
  if (interval === 'week') {
    const weekday = (new Date(day * 1000).getUTCDay() + 6) % 7; // Monday = 0
    return day - weekday * DAY;
  }
  const date = new Date(day * 1000);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1) / 1000;
};

const nextBucket = (unix, interval) => {
  if (interval === 'hour') return unix + HOUR;
  if (interval === 'day') return unix + DAY;
  if (interval === 'week') return unix + 7 * DAY;
  return addMonths(unix, 1);
};

// --- metrics ------------------------------------------------------------------------------

// A chart's own fields: one of the seven types, and a boolean legend flag (`null` is refused).
const checkChartFields = body => {
  if (body.viz_type !== undefined && !ENABLED_TYPES.includes(body.viz_type)) {
    return ['invalid_viz_type', `Unknown chart type ${body.viz_type}.`];
  }
  if (body.show_legend !== undefined && typeof body.show_legend !== 'boolean') {
    return ['invalid_show_legend', 'show_legend must be true or false.'];
  }
  return null;
};

// Same rules as the backend: `value` mode is exactly one metric, `key` mode 1 to 10 distinct ones.
const checkKeys = (seriesBy, keys) => {
  const list = Array.isArray(keys) ? keys : [];
  const distinct = new Set(list).size === list.length;
  if (seriesBy === 'value') {
    return list.length === 1
      ? null
      : ['invalid_metric_keys', 'A single metric is needed.'];
  }
  if (!list.length || list.length > MAX_METRIC_KEYS || !distinct) {
    return ['invalid_metric_keys', 'Pick 1 to 10 different metrics.'];
  }
  return null;
};

// --- chart data -----------------------------------------------------------------------------

const hiddenSplit = (totals, hiddenValues) => {
  const hidden = hiddenValues.map(value => ({
    value,
    count: totals.find(t => t.value === value)?.count || 0,
  }));
  const visible = totals
    .filter(t => !hiddenValues.includes(t.value))
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
  return { hidden, visible };
};

const daysBetween = (from, to) => {
  const days = [];
  for (let d = startOfUtcDay(from); d < to && days.length < 420; d += DAY) {
    days.push(d);
  }
  return days;
};

// Counts of one value of one metric, per bucket.
const valueCounts = ({
  key,
  item,
  source,
  interval,
  starts,
  days,
  from,
  to,
}) => {
  const counts = starts.map(() => 0);
  if (interval === 'hour') {
    for (let h = bucketStart(from, 'hour'); h < to; h += HOUR) {
      counts[starts.indexOf(h)] += hourCount(key, item, h, source.spiky);
    }
  } else {
    days.forEach(day => {
      const index = starts.indexOf(bucketStart(day, interval));
      if (index >= 0) counts[index] += dayCount(key, item, day, source.spiky);
    });
  }
  return counts;
};

const sum = list => list.reduce((total, n) => total + n, 0);

const emptyData = (base, vizType, interval, hiddenValues) => {
  const hidden = hiddenValues.map(value => ({ value, count: 0 }));
  return isBarShape(vizType)
    ? {
        ...base,
        interval,
        buckets: [],
        series: [],
        other: null,
        total: 0,
        hidden,
      }
    : { ...base, total: 0, values: [], other_count: 0, hidden };
};

// What a chart draws, in the backend's shapes. `entries` are the things shown: the values of the
// first key in `value` mode, the keys themselves in `key` mode.
const buildData = ({
  seriesBy,
  keys,
  vizType,
  timeRange,
  hiddenValues,
  maxValues,
  now,
}) => {
  const resolved = resolveRange(timeRange, now);
  if (resolved.error) return { error: resolved.error };
  const { from, to } = resolved;
  const interval = autoInterval(timeRange, from, to);
  const base = {
    key: keys[0],
    viz_type: vizType,
    from: toIso(from),
    to: toIso(to),
    tz: 'UTC',
  };
  const days = daysBetween(from, to);

  const starts = [];
  if (isBarShape(vizType)) {
    for (
      let b = bucketStart(from, interval);
      b < to && starts.length < 800;
      b = nextBucket(b, interval)
    ) {
      starts.push(b);
    }
  } else {
    starts.push(0);
  }

  // One entry per value (value mode) or per metric (key mode), each with counts per bucket.
  const entries =
    seriesBy === 'key'
      ? keys.map(key => {
          const source = SOURCES[key];
          const perBucket = starts.map(() => 0);
          (source?.values || []).forEach(item => {
            const counts = valueCounts({
              key,
              item,
              source,
              interval,
              starts: isBarShape(vizType) ? starts : [0],
              days,
              from,
              to,
            });
            counts.forEach((n, i) => {
              perBucket[i] += n;
            });
          });
          return { value: key, counts: perBucket };
        })
      : (SOURCES[keys[0]]?.values || []).map(item => ({
          value: item.value,
          counts: valueCounts({
            key: keys[0],
            item,
            source: SOURCES[keys[0]],
            interval,
            starts: isBarShape(vizType) ? starts : [0],
            days,
            from,
            to,
          }),
        }));

  if (!entries.length) {
    return { data: emptyData(base, vizType, interval, hiddenValues) };
  }

  // Non-bar charts only need the total per entry; for those `starts` is one dummy bucket and the
  // day loop above is not bucketed, so total them from days directly.
  const totals = entries.map(entry => ({
    value: entry.value,
    counts: entry.counts,
    count: 0,
  }));
  if (!isBarShape(vizType)) {
    totals.forEach(entry => {
      const source =
        seriesBy === 'key' ? SOURCES[entry.value] : SOURCES[keys[0]];
      const items =
        seriesBy === 'key'
          ? source?.values || []
          : source.values.filter(item => item.value === entry.value);
      const key = seriesBy === 'key' ? entry.value : keys[0];
      // eslint-disable-next-line no-param-reassign
      entry.count = sum(
        items.map(item =>
          sum(days.map(day => dayCount(key, item, day, source.spiky)))
        )
      );
    });
  } else {
    totals.forEach(entry => {
      // eslint-disable-next-line no-param-reassign
      entry.count = sum(entry.counts);
    });
  }

  const { hidden, visible } = hiddenSplit(totals, hiddenValues);
  const top = visible.slice(0, maxValues);
  const rest = visible.slice(maxValues);

  if (!isBarShape(vizType)) {
    const otherCount = sum(rest.map(item => item.count));
    return {
      data: {
        ...base,
        total: sum(top.map(item => item.count)) + otherCount,
        values: top.map(({ value, count }) => ({ value, count })),
        other_count: otherCount,
        hidden: hidden.map(({ value, count }) => ({ value, count })),
      },
    };
  }

  const other = rest.length
    ? { counts: starts.map((_, i) => sum(rest.map(v => v.counts[i]))) }
    : null;
  return {
    data: {
      ...base,
      interval,
      buckets: starts.map(toIso),
      series: top.map(({ value, counts }) => ({ value, counts })),
      other,
      total: sum(visible.map(item => item.count)),
      hidden: hidden.map(({ value, count }) => ({ value, count })),
    },
  };
};

// --- stored charts --------------------------------------------------------------------------

const storeKey = accountId => `customAnalyticsMockCharts:v5:${accountId}`;

const starterCharts = () => {
  const chart = (
    id,
    title,
    keys,
    vizType,
    timeRange,
    layout,
    seriesBy = 'value',
    showLegend = true
  ) => ({
    id,
    title,
    series_by: seriesBy,
    metric_key: keys[0],
    metric_keys: keys,
    viz_type: vizType,
    show_legend: showLegend,
    time_range: timeRange,
    inbox_ids: [],
    hidden_values: [],
    layout,
  });
  const days30 = { preset: 'last_30_days' };
  return [
    chart(
      1,
      'Last Month Sessions',
      ['Bot Messages', 'User Messages', 'Sessions'],
      'bar',
      days30,
      { x: 0, y: 0, w: 8, h: 7 },
      'key'
    ),
    chart(
      2,
      'User Types',
      ['New Users', 'Returning Users'],
      'pie',
      days30,
      { x: 8, y: 0, w: 4, h: 7 },
      'key'
    ),
    chart(
      3,
      'Rating',
      ['Rating'],
      'metric',
      { preset: 'last_year' },
      { x: 0, y: 7, w: 4, h: 6 }
    ),
    chart(
      4,
      'Rating per week',
      ['Rating'],
      'bar',
      { preset: 'last_year' },
      { x: 4, y: 7, w: 8, h: 6 }
    ),
    chart(5, 'Star Rating', ['Star Rating'], 'pie', days30, {
      x: 0,
      y: 13,
      w: 4,
      h: 6,
    }),
    chart(6, 'Country', ['country'], 'bar', days30, {
      x: 4,
      y: 13,
      w: 8,
      h: 6,
    }),
    chart(
      7,
      'Total Messages',
      ['Bot Messages', 'User Messages'],
      'metric',
      days30,
      { x: 0, y: 19, w: 6, h: 5 },
      'key'
    ),
    chart(
      8,
      'Messages over time',
      ['Bot Messages', 'User Messages', 'Sessions'],
      'area',
      days30,
      { x: 0, y: 24, w: 8, h: 6 },
      'key'
    ),
    chart(9, 'Star Rating split', ['Star Rating'], 'donut', days30, {
      x: 8,
      y: 24,
      w: 4,
      h: 6,
    }),
    chart(
      10,
      'Sessions trend',
      ['Sessions'],
      'spark_area',
      days30,
      { x: 0, y: 30, w: 6, h: 5 },
      'key',
      false
    ),
    chart(
      11,
      'Messages (spark)',
      ['Bot Messages', 'User Messages'],
      'spark_bar',
      days30,
      { x: 6, y: 30, w: 6, h: 5 },
      'key'
    ),
  ];
};

const load = accountId => {
  try {
    const saved = window.localStorage.getItem(storeKey(accountId));
    if (saved) return JSON.parse(saved);
  } catch (e) {
    // fall through to the starter charts
  }
  return { nextId: 12, charts: starterCharts() };
};

const save = (accountId, state) => {
  try {
    window.localStorage.setItem(storeKey(accountId), JSON.stringify(state));
  } catch (e) {
    // storage blocked: changes just do not persist in mock mode
  }
};

const withData = (stored, now) => {
  const result = buildData({
    seriesBy: stored.series_by,
    keys: stored.metric_keys,
    vizType: stored.viz_type,
    timeRange: stored.time_range,
    hiddenValues: stored.hidden_values || [],
    maxValues: TOP_VALUES,
    now,
  });
  const stamp = new Date(now * 1000).toISOString();
  return {
    ...stored,
    created_by_user_id: null,
    updated_by_user_id: null,
    created_at: stored.created_at || stamp,
    updated_at: stamp,
    data: result.error ? null : result.data,
    error: result.error
      ? { code: 'query_failed', message: result.error[1] }
      : null,
  };
};

const nowUnix = () => Math.floor(Date.now() / 1000);

const validTimeRange = (timeRange, now) => {
  if (!timeRange) return null;
  const resolved = resolveRange(timeRange, now);
  return resolved.error || null;
};

export const mockCustomAnalytics = {
  getCharts(accountId) {
    const state = load(accountId);
    const now = nowUnix();
    const charts = state.charts
      .map(c => withData(c, now))
      .sort(
        (a, b) =>
          a.layout.y - b.layout.y || a.layout.x - b.layout.x || a.id - b.id
      );
    return respond({ charts });
  },

  getKeys() {
    return respond({ keys: Object.keys(SOURCES).sort(), truncated: false });
  },

  getValues(accountId, params = {}) {
    const source = SOURCES[params.key];
    const result = buildData({
      seriesBy: 'value',
      keys: [params.key],
      vizType: 'pie',
      timeRange: {
        preset: params.preset || 'last_year',
        from: params.from,
        to: params.to,
      },
      hiddenValues: [],
      maxValues: 500,
      now: nowUnix(),
    });
    if (result.error) return fail(422, result.error[0], result.error[1]);
    return respond({
      key: params.key,
      from: result.data.from,
      to: result.data.to,
      values: source ? result.data.values : [],
      truncated: false,
    });
  },

  query(accountId, body) {
    const seriesBy = body.series_by || 'value';
    const keys = seriesBy === 'key' ? body.keys : [body.key];
    const keyError = checkKeys(seriesBy, keys);
    if (keyError) return fail(422, keyError[0], keyError[1]);
    const result = buildData({
      seriesBy,
      keys,
      vizType: body.viz_type,
      timeRange: body.time_range,
      hiddenValues: body.hidden_values || [],
      maxValues: body.max_values || TOP_VALUES,
      now: nowUnix(),
    });
    if (result.error) return fail(422, result.error[0], result.error[1]);
    return respond(result.data);
  },

  createChart(accountId, body) {
    const state = load(accountId);
    const now = nowUnix();
    if (state.charts.length >= MAX_CHARTS) {
      return fail(
        422,
        'chart_limit_reached',
        'A dashboard can have at most 50 charts.'
      );
    }
    const seriesBy = body.series_by || 'value';
    const keys = seriesBy === 'key' ? body.metric_keys : [body.metric_key];
    const keyError = checkKeys(seriesBy, keys);
    if (keyError) return fail(422, keyError[0], keyError[1]);
    const rangeError = validTimeRange(body.time_range, now);
    if (rangeError) return fail(422, rangeError[0], rangeError[1]);
    const fieldError = checkChartFields(body);
    if (fieldError) return fail(422, fieldError[0], fieldError[1]);
    const bottom = state.charts.reduce(
      (max, c) => Math.max(max, c.layout.y + c.layout.h),
      0
    );
    const stored = {
      id: state.nextId,
      title: (body.title || keys.join(', ')).slice(0, 100),
      series_by: seriesBy,
      metric_key: keys[0],
      metric_keys: keys,
      viz_type: body.viz_type,
      show_legend: body.show_legend ?? true,
      time_range: body.time_range || { preset: 'last_30_days' },
      inbox_ids: body.inbox_ids || [],
      hidden_values: body.hidden_values || [],
      layout: body.layout || { x: 0, y: bottom, w: 6, h: 4 },
      created_at: new Date(now * 1000).toISOString(),
    };
    save(accountId, {
      nextId: state.nextId + 1,
      charts: [...state.charts, stored],
    });
    return respond(withData(stored, now), 201);
  },

  updateChart(accountId, id, body) {
    const state = load(accountId);
    const now = nowUnix();
    const current = state.charts.find(c => c.id === Number(id));
    if (!current) {
      return fail(404, 'chart_not_found', `Chart ${id} was not found.`);
    }
    const rangeError = validTimeRange(body.time_range, now);
    if (rangeError) return fail(422, rangeError[0], rangeError[1]);
    const fieldError = checkChartFields(body);
    if (fieldError) return fail(422, fieldError[0], fieldError[1]);

    // The backend's PATCH rules for the metrics.
    const seriesBy = body.series_by || current.series_by;
    let keys = current.metric_keys;
    if (body.metric_keys) {
      keys = body.metric_keys;
    } else if (body.metric_key !== undefined) {
      if (seriesBy === 'key' && body.metric_key !== keys[0]) {
        return fail(
          422,
          'invalid_metric_keys',
          'Send metric_keys to change the list.'
        );
      }
      if (seriesBy === 'value') keys = [body.metric_key];
    }
    if (seriesBy === 'value' && !body.metric_keys) keys = keys.slice(0, 1);
    const keyError = checkKeys(seriesBy, keys);
    if (keyError) return fail(422, keyError[0], keyError[1]);

    const {
      metric_key: ignoredKey, // eslint-disable-line no-unused-vars
      metric_keys: ignoredKeys, // eslint-disable-line no-unused-vars
      ...rest
    } = body;
    const next = {
      ...current,
      ...rest,
      series_by: seriesBy,
      metric_key: keys[0],
      metric_keys: keys,
    };
    save(accountId, {
      ...state,
      charts: state.charts.map(c => (c.id === current.id ? next : c)),
    });
    return respond(withData(next, now));
  },

  deleteChart(accountId, id) {
    const state = load(accountId);
    if (!state.charts.some(c => c.id === Number(id))) {
      return fail(404, 'chart_not_found', `Chart ${id} was not found.`);
    }
    save(accountId, {
      ...state,
      charts: state.charts.filter(c => c.id !== Number(id)),
    });
    return respond({}, 204);
  },

  saveLayout(accountId, { items = [] } = {}) {
    const state = load(accountId);
    const ids = items.map(i => i.id);
    if (new Set(ids).size !== ids.length) {
      return fail(422, 'invalid_layout', 'Duplicate chart ids.');
    }
    const missing = items.find(i => !state.charts.some(c => c.id === i.id));
    if (missing) {
      return fail(404, 'chart_not_found', `Chart ${missing.id} was not found.`);
    }
    const bad = items.find(
      i =>
        i.x < 0 ||
        i.w < 1 ||
        i.x + i.w > 12 ||
        i.y < 0 ||
        i.y > 1000 ||
        i.h < 1 ||
        i.h > 24
    );
    if (bad) {
      return fail(422, 'invalid_layout', `Chart ${bad.id} is out of bounds.`);
    }
    const charts = state.charts.map(c => {
      const item = items.find(i => i.id === c.id);
      return item
        ? { ...c, layout: { x: item.x, y: item.y, w: item.w, h: item.h } }
        : c;
    });
    save(accountId, { ...state, charts });
    return respond({
      layouts: charts.map(c => ({ id: c.id, ...c.layout })),
    });
  },
};
